import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  ScrollView,
  TextInput,
  Switch,
  ActivityIndicator,
  Pressable,
  Alert,
  useColorScheme,
} from 'react-native';
import {
  CompositeNavigationProp,
  useFocusEffect,
  useNavigation,
} from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { BottomTabNavigationProp } from '@react-navigation/bottom-tabs';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { BadgeChips } from '../../components/BadgeChips';
import { ModalBottomSheet } from '../../components/ModalBottomSheet';
import { ForumListSkeleton } from '../../components/skeletons/ScreenSkeletons';
import { useAuth } from '../../contexts/AuthContext';
import { useMiniProfiles, type MiniProfile } from '../../hooks/useMiniProfiles';
import type { MainStackParamList, MainTabParamList } from '../../navigation/types';
import { syncBilgeAnnedasBadge } from '../../services/badgeSyncService';
import { beautifyForumText } from '../../services/geminiService';
import { createForumPost, subscribeForumPosts, toggleLike } from '../../services/forumService';
import { getBlockedUserIdsSet } from '../../services/userService';
import { showGuestRegisterPrompt } from '../../utils/guestReadOnlyAlert';
import {
  isForumPostLocallyHidden,
  subscribeForumLocalHideChanges,
} from '../../utils/forumReportLocalHide';
import { impactLight } from '../../utils/haptics';
import {
  FORUM_CATEGORIES,
  type ForumCategory,
  type ForumPostDoc,
} from '../../types/forum';

const FILTER_ALL = 'Tümü' as const;

type ForumScreenNav = CompositeNavigationProp<
  BottomTabNavigationProp<MainTabParamList, 'Forum'>,
  NativeStackNavigationProp<MainStackParamList>
>;

function formatPostDate(createdAt: ForumPostDoc['createdAt']): string {
  try {
    const d = createdAt?.toDate?.() ?? new Date();
    return d.toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

export function ForumScreen() {
  const insets = useSafeAreaInsets();
  const colorScheme = useColorScheme();
  const navigation = useNavigation<ForumScreenNav>();
  const { user, logout, profile } = useAuth();
  const isGuest = Boolean(user?.isAnonymous);
  const [posts, setPosts] = useState<ForumPostDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState<ForumCategory | typeof FILTER_ALL>(
    FILTER_ALL,
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftContent, setDraftContent] = useState('');
  const [draftCategory, setDraftCategory] = useState<ForumCategory>('Genel');
  const [draftAnonymous, setDraftAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [beautifyPostBusy, setBeautifyPostBusy] = useState(false);
  const [hideForumEpoch, bumpForumHide] = useReducer((n: number) => n + 1, 0);

  useEffect(() => subscribeForumLocalHideChanges(() => bumpForumHide()), []);

  useEffect(() => {
    const unsub = subscribeForumPosts(
      list => {
        setPosts(list);
        setLoading(false);
      },
      () => setLoading(false),
    );
    return unsub;
  }, []);

  useFocusEffect(
    useCallback(() => {
      if (user?.uid && !user.isAnonymous) {
        void syncBilgeAnnedasBadge(user.uid);
      }
    }, [user?.uid, user?.isAnonymous]),
  );

  const blockedUserIds = useMemo(() => getBlockedUserIdsSet(profile), [profile?.blockedUsers]);

  const visiblePosts = useMemo(() => {
    let list = categoryFilter === FILTER_ALL ? posts : posts.filter(p => p.category === categoryFilter);
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter(p => p.title.toLowerCase().includes(q));
    }
    list = list.filter(p => !blockedUserIds.has(p.authorId));
    list = list.filter(p => !isForumPostLocallyHidden(p.id));
    return list;
  }, [posts, categoryFilter, searchQuery, blockedUserIds, hideForumEpoch]);

  const authorIds = useMemo(
    () => visiblePosts.filter(p => !p.isAnonymous).map(p => p.authorId),
    [visiblePosts],
  );
  const miniProfiles = useMiniProfiles(authorIds);

  const openModal = useCallback(() => {
    void impactLight();
    if (isGuest) {
      showGuestRegisterPrompt(logout);
      return;
    }
    setDraftTitle('');
    setDraftContent('');
    setDraftCategory('Genel');
    setDraftAnonymous(false);
    setFormError(null);
    setModalOpen(true);
  }, [isGuest, logout]);

  const onTogglePostLike = useCallback(
    async (postId: string) => {
      if (isGuest) {
        showGuestRegisterPrompt(logout);
        return;
      }
      if (!user?.uid) {
        return;
      }
      void impactLight();
      try {
        await toggleLike(postId, user.uid);
      } catch {
        /* sessiz */
      }
    },
    [isGuest, logout, user?.uid, posts],
  );

  const canBeautifyPostContent = draftContent.trim().length >= 10;

  const onBeautifyPostContent = useCallback(async () => {
    if (!canBeautifyPostContent || beautifyPostBusy || submitting) {
      return;
    }
    void impactLight();
    setBeautifyPostBusy(true);
    setFormError(null);
    try {
      const next = await beautifyForumText(draftContent, 'post');
      setDraftContent(next);
    } catch (e) {
      Alert.alert(
        'Güzelleştirilemedi',
        e instanceof Error ? e.message : 'Bağlantıyı kontrol edip tekrar dene.',
      );
    } finally {
      setBeautifyPostBusy(false);
    }
  }, [draftContent, canBeautifyPostContent, beautifyPostBusy, submitting]);

  const submitPost = useCallback(async () => {
    if (isGuest) {
      setFormError('Misafir olarak gönderi açamazsın; kayıt ol.');
      return;
    }
    if (!user?.uid) {
      setFormError('Oturum gerekli.');
      return;
    }
    const t = draftTitle.trim();
    const c = draftContent.trim();
    if (t.length < 3) {
      setFormError('Başlık en az 3 karakter olsun.');
      return;
    }
    if (c.length < 10) {
      setFormError('İçerik en az 10 karakter olsun.');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      await createForumPost({
        authorId: user.uid,
        isAnonymous: draftAnonymous,
        title: t,
        content: c,
        category: draftCategory,
      });
      void impactLight();
      setModalOpen(false);
    } catch {
      setFormError('Gönderi kaydedilemedi. Tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  }, [isGuest, user?.uid, draftTitle, draftContent, draftCategory, draftAnonymous]);

  const chipFilters: (ForumCategory | typeof FILTER_ALL)[] = [FILTER_ALL, ...FORUM_CATEGORIES];

  return (
 <View className="flex-1 bg-white dark:bg-black" style={{ paddingTop: insets.top }}>
      <View className="border-b border-zinc-200 px-4 pb-0 pt-3 dark:border-zinc-800">
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ flexDirection: 'row', alignItems: 'center', paddingBottom: 0 }}>
          {chipFilters.map(cat => {
            const selected = categoryFilter === cat;
            return (
              <Pressable
                key={cat}
                onPress={() =>
                  setCategoryFilter(cat === FILTER_ALL ? FILTER_ALL : (cat as ForumCategory))
                }
                className={`mr-5 pb-2.5 ${
                  selected
                    ? 'border-b-2 border-zinc-900 dark:border-zinc-100'
                    : 'border-b-2 border-transparent'
                }`}>
                <Text
                  className={`text-sm font-semibold ${
                    selected ? 'text-zinc-900 dark:text-zinc-100' : 'text-zinc-500 dark:text-zinc-500'
                  }`}>
                  {cat}
                </Text>
              </Pressable>
            );
          })}
        </ScrollView>
        <View className="px-4 pb-3 pt-1">
          <TextInput
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholder="Başlıkta ara…"
            placeholderTextColor="#9ca3af"
            className="rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2.5 text-sm text-zinc-900 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-100"
            autoCorrect={false}
            autoCapitalize="none"
          />
        </View>
      </View>

      {loading ? (
        <ForumListSkeleton />
      ) : (
        <FlatList
          data={visiblePosts}
          keyExtractor={item => item.id}
          contentContainerStyle={{
            paddingHorizontal: 16,
            paddingTop: 8,
            paddingBottom: 32 + insets.bottom + 112,
          }}
          ListEmptyComponent={
 <Text className="mt-8 text-center text-sm text-gray-500 dark:text-zinc-400">
              Bu filtrede henüz gönderi yok. İlk soruyu sen sor.
            </Text>
          }
          renderItem={({ item }) => (
            <ForumPostCard
              item={item}
              authorMini={item.isAnonymous ? undefined : miniProfiles[item.authorId]}
              currentUserId={user?.uid}
              onOpen={() => navigation.navigate('ForumPostDetail', { postId: item.id })}
              onToggleLike={() => void onTogglePostLike(item.id)}
            />
          )}
        />
      )}

      <ModalBottomSheet
        visible={modalOpen}
        onRequestClose={() => setModalOpen(false)}
        dismissDisabled={submitting || beautifyPostBusy}
        keyboardAvoiding>
        <View
          className="rounded-t-3xl bg-white px-5 pt-6 dark:bg-zinc-900"
          style={{ paddingBottom: 24 + insets.bottom }}>
 <Text className="mb-4 text-xl font-bold text-gray-800 dark:text-zinc-100">Yeni soru</Text>

        <Text className="mb-2 text-sm font-medium text-gray-700 dark:text-zinc-300">Kategori</Text>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              className="mb-4 border-b border-zinc-200 dark:border-zinc-700">
              <View className="flex-row">
                {FORUM_CATEGORIES.map(cat => {
                  const on = draftCategory === cat;
                  return (
                    <Pressable
                      key={cat}
                      onPress={() => setDraftCategory(cat)}
                      className={`mr-4 pb-2 ${
                        on
                          ? 'border-b-2 border-zinc-900 dark:border-zinc-100'
                          : 'border-b-2 border-transparent'
                      }`}>
                      <Text
                        className={`text-sm font-semibold ${
                          on ? 'text-zinc-900 dark:text-zinc-100' : 'text-zinc-500 dark:text-zinc-500'
                        }`}>
                        {cat}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
            </ScrollView>

            <Text className="mb-1 text-sm font-medium text-gray-700 dark:text-zinc-300">Başlık</Text>
            <TextInput
              value={draftTitle}
              onChangeText={setDraftTitle}
              placeholder="Kısa bir başlık"
              placeholderTextColor="#9ca3af"
              className="mb-3 border-b border-zinc-200 py-2 text-base text-gray-800 dark:border-zinc-700 dark:text-zinc-100"
            />

            <Text className="mb-1 text-sm font-medium text-gray-700 dark:text-zinc-300">İçerik</Text>
            <TextInput
              value={draftContent}
              onChangeText={setDraftContent}
              placeholder="Detayı buraya yaz…"
              placeholderTextColor="#9ca3af"
              multiline
              textAlignVertical="top"
              editable={!beautifyPostBusy}
              className="mb-2 min-h-[100px] border-b border-zinc-200 py-2 text-base text-gray-800 dark:border-zinc-700 dark:text-zinc-100"
            />
            <TouchableOpacity
              activeOpacity={0.88}
              disabled={!canBeautifyPostContent || beautifyPostBusy || submitting}
              onPress={() => void onBeautifyPostContent()}
              className={`mb-4 rounded-xl border px-3 py-2.5 ${
                !canBeautifyPostContent || submitting
                  ? 'border-zinc-200 bg-zinc-50 opacity-60 dark:border-zinc-800 dark:bg-zinc-900/60'
                  : 'border-zinc-300 bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-800'
              }`}>
              {beautifyPostBusy ? (
                <ActivityIndicator size="small" color={colorScheme === 'dark' ? '#fafafa' : '#18181b'} />
              ) : (
                <Text className="text-center text-sm font-semibold text-zinc-800 dark:text-zinc-200">
                  Metni güzelleştir
                </Text>
              )}
            </TouchableOpacity>

            <View className="mb-4 flex-row items-center justify-between rounded-xl border border-zinc-200 bg-transparent px-3 py-2.5 dark:border-zinc-800">
 <View className="flex-1 pr-3">
 <Text className="text-base font-semibold text-gray-800 dark:text-zinc-100">
                  Anonim paylaş
                </Text>
 <Text className="text-xs text-gray-500 dark:text-zinc-400">
                  Adın görünmez; &quot;Bir Annedaş&quot; olarak listelenirsin.
                </Text>
              </View>
              <Switch
                value={draftAnonymous}
                onValueChange={setDraftAnonymous}
                trackColor={{ false: '#e7e5e4', true: '#fdba74' }}
                thumbColor={draftAnonymous ? '#fff7ed' : '#f4f4f5'}
              />
            </View>

            {formError ? (
 <Text className="mb-3 text-center text-sm text-zinc-700">{formError}</Text>
            ) : null}

            <View className="flex-row gap-3">
              <TouchableOpacity
                onPress={() => setModalOpen(false)}
                className="flex-1 rounded-xl border border-zinc-300 bg-white py-2.5 dark:border-zinc-800 dark:bg-zinc-900">
                <Text className="text-center text-sm font-semibold text-gray-600 dark:text-zinc-300">
                  Vazgeç
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={submitPost}
                disabled={submitting}
                className="flex-1 rounded-xl border border-zinc-900 bg-zinc-900 py-2.5 dark:border-zinc-50 dark:bg-zinc-50">
                {submitting ? (
                  <ActivityIndicator color={colorScheme === 'dark' ? '#18181b' : '#fff'} />
                ) : (
                  <Text className="text-center font-semibold text-white dark:text-zinc-950">Yayınla</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
      </ModalBottomSheet>

      <TouchableOpacity
        onPress={openModal}
        activeOpacity={0.92}
        accessibilityLabel="Yeni soru sor"
        className="absolute h-14 w-14 items-center justify-center rounded-full border border-zinc-200 bg-zinc-900 dark:border-zinc-700 dark:bg-zinc-50"
        style={{
          bottom: 20 + insets.bottom,
          right: 16,
        }}>
        <Text className="text-xl text-white dark:text-zinc-900" style={{ marginTop: 2 }}>
          ✍️
        </Text>
      </TouchableOpacity>

    </View>
  );
}

function ForumPostCard({
  item,
  authorMini,
  currentUserId,
  onOpen,
  onToggleLike,
}: {
  item: ForumPostDoc;
  authorMini?: MiniProfile;
  currentUserId?: string;
  onOpen: () => void;
  onToggleLike: () => void;
}) {
  const preview =
    item.content.length > 160 ? `${item.content.slice(0, 160)}…` : item.content;
  const liked = Boolean(currentUserId && item.likedBy.includes(currentUserId));
  const likeCount = item.likedBy.length;
  const displayName = authorMini?.displayName ?? 'Bir Annedaş';

  return (
    <View className="mb-4 rounded-xl border border-zinc-200 bg-white px-4 py-3.5 dark:border-zinc-800 dark:bg-zinc-900">
      <TouchableOpacity activeOpacity={0.88} onPress={onOpen}>
 <View className="mb-2 flex-row items-center justify-between">
          <View className="rounded-full border border-zinc-200 bg-zinc-50 px-2.5 py-1 dark:border-zinc-700 dark:bg-zinc-800">
            <Text className="text-xs font-semibold text-zinc-700 dark:text-zinc-200">
              {item.category}
            </Text>
          </View>
 <Text className="text-xs text-gray-400 dark:text-zinc-500">{formatPostDate(item.createdAt)}</Text>
        </View>

 <Text className="mb-2 text-base font-bold text-gray-800 dark:text-zinc-100">{item.title}</Text>
 <Text className="mb-4 text-sm leading-5 text-gray-500 dark:text-zinc-400">{preview}</Text>
      </TouchableOpacity>

 <View className="flex-row items-start border-t border-zinc-200 pt-3 dark:border-zinc-800">
        {item.isAnonymous ? (
          <>
            <View
 className="mr-3 h-10 w-10 items-center justify-center rounded-full border border-zinc-200 dark:border-zinc-800"
              style={{ backgroundColor: '#fff1f2' }}>
              <Text style={{ fontSize: 20 }}>🌸</Text>
            </View>
 <View className="min-w-0 flex-1">
 <Text className="text-sm font-semibold text-gray-700 dark:text-zinc-200">Bir Annedaş</Text>
 <Text className="text-xs text-gray-400 dark:text-zinc-500">Anonim paylaşım</Text>
            </View>
          </>
        ) : (
          <>
            <View
 className="mr-3 h-10 w-10 items-center justify-center rounded-full border border-zinc-200 dark:border-zinc-800"
              style={{ backgroundColor: '#fff7ed' }}>
              <Text style={{ fontSize: 18 }}>👤</Text>
            </View>
 <View className="min-w-0 flex-1">
 <View className="flex-row flex-wrap items-center gap-x-0.5">
                <Text
 className="text-sm font-semibold text-gray-700 dark:text-zinc-200"
                  numberOfLines={1}>
                  {displayName}
                </Text>
                <BadgeChips badges={authorMini?.badges} compact />
              </View>
 <Text className="text-xs text-gray-400 dark:text-zinc-500">Üye</Text>
            </View>
          </>
        )}

 <View className="flex-row items-center gap-3 self-center">
          <TouchableOpacity
            onPress={() => onToggleLike()}
            disabled={!currentUserId}
            activeOpacity={0.75}
 className="flex-row items-center rounded-xl border border-zinc-200 bg-zinc-50 dark:bg-zinc-950 px-2.5 py-1.5 dark:border-zinc-800 dark:bg-zinc-900">
 <Text className="text-sm" style={{ opacity: currentUserId ? 1 : 0.45 }}>
              {liked ? '🌹' : '🤍'}
            </Text>
            <Text
              className={`ml-1 text-xs font-semibold ${
                liked ? 'text-zinc-700 dark:text-zinc-300' : 'text-zinc-500 dark:text-zinc-400'
              }`}>
              {likeCount}
            </Text>
          </TouchableOpacity>
 <TouchableOpacity onPress={onOpen} activeOpacity={0.75} className="flex-row items-center">
 <Text className="text-xs text-gray-400 dark:text-zinc-500">💬 {item.commentCount}</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
}
